import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "../../utils/api/axios";
import useUserCredentials from "../../utils/hooks/user/useUserCredentials";
import { UserRole } from "../../utils/api/endpoints/user/credentials";

export interface UserOption {
  user_id: number;
  username: string;
  display_name?: string;
  email?: string;
}

interface UserSearchDropdownProps {
  value?: number;
  onChange: (userId: number, user: UserOption) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

const UserSearchDropdown: React.FC<UserSearchDropdownProps> = ({
  onChange,
  placeholder = "Search by username or email...",
  className = "",
  disabled = false
}) => {
  const { role } = useUserCredentials();
  const isAdminGroup = [UserRole.ADMIN, UserRole.SUB_ADMIN, UserRole.ADMIN_MANAGER, UserRole.QA].includes(role as UserRole);

  const [searchQuery, setSearchQuery] = useState("");
  const [users, setUsers] = useState<UserOption[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout>();

  // Debounced search function
  const searchUsers = useCallback(async (query: string) => {
    // Only allow admin roles to search
    if (!isAdminGroup) {
      console.warn("Unauthorized: Only admin roles can search researchers");
      setUsers([]);
      return;
    }

    if (!query || query.trim().length < 2) {
      setUsers([]);
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.get("/v2/user/search-researchers", {
        params: {
          q: query.trim(),
          limit: 10
        }
      });

      // Backend returns array of users directly
      const userData = Array.isArray(response.data) ? response.data : [];
      const mappedUsers = userData.map((user: any) => ({
        user_id: user.user_id,
        username: user.username,
        display_name: user.display_name,
        email: user.email
      }));
      setUsers(mappedUsers);
    } catch (error) {
      console.error("Error searching researchers:", error);
      setUsers([]);
    } finally {
      setIsLoading(false);
    }
  }, [isAdminGroup]);

  // Handle input change with debounce
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);

    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Set new timeout for debounced search
    timeoutRef.current = setTimeout(() => {
      searchUsers(query);
    }, 300);
  };

  // Handle user selection
  const handleSelectUser = (user: UserOption) => {
    setSearchQuery(user.display_name || user.username);
    setIsOpen(false);
    onChange(user.user_id, user);
  };

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <div ref={dropdownRef} className={`relative ${className}`}>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={searchQuery}
          onChange={handleInputChange}
          onFocus={() => setIsOpen(true)}
          placeholder={placeholder}
          disabled={disabled}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
        />
        {isLoading && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <div className="animate-spin h-4 w-4 border-2 border-indigo-600 border-t-transparent rounded-full"></div>
          </div>
        )}
      </div>

      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-50 max-h-64 overflow-y-auto">
          {isLoading ? (
            <div className="px-4 py-3 text-sm text-gray-500 text-center">Searching...</div>
          ) : users.length > 0 ? (
            users.map((user) => (
              <button
                key={user.user_id}
                onClick={() => handleSelectUser(user)}
                className="w-full text-left px-4 py-2 hover:bg-indigo-50 transition-colors border-b border-gray-100 last:border-b-0"
              >
                <div className="font-medium text-gray-900">{user.display_name || user.username}</div>
                <div className="text-xs text-gray-500">@{user.username}</div>
                {user.email && <div className="text-xs text-gray-400">{user.email}</div>}
              </button>
            ))
          ) : searchQuery.length >= 2 ? (
            <div className="px-4 py-3 text-sm text-gray-500 text-center">No users found</div>
          ) : (
            <div className="px-4 py-3 text-sm text-gray-500 text-center">Type at least 2 characters to search</div>
          )}
        </div>
      )}
    </div>
  );
};

export default UserSearchDropdown;

